





























































































import { Vue, Component, Prop, Watch, Emit, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { FilteredEvent } from "@/store/eventqueue/types";
import api from "@/services/api.service";
import { Response } from "@/store/responses/types";
import SureViewIcon from "@/components/SureViewIcon.vue";
import { Metadata } from "@/store/metadata/types";
import LinkedTasksMixin from "@/mixins/LinkedTasksMixin";

const EventQueueStore = namespace("eventqueue");
const MetadataStore = namespace("metadata");
const SiteMonitorStore = namespace("siteMonitor");

type ResponseMetadataMap = {
	[responseId: number]: Metadata[];
};

@Component({
	components: {
		"sureview-icon": SureViewIcon,
	},
})
export default class LinkedTaskModal extends Mixins(LinkedTasksMixin) {
	@MetadataStore.Action private deleteMetadata: (metadataToDelete: Metadata) => Promise<void>;
	@SiteMonitorStore.Action private createAuditRecord: ({
		eventId,
		eventRecordTypeId,
		details,
		objectId,
	}) => Promise<number>;

	@EventQueueStore.Mutation private setLinkedTaskModalShown: ({ visible, event, responsesMetadata }) => void;
	@EventQueueStore.State("linkedTasksModal") private state: {
		visible: boolean;
		event: FilteredEvent;
		responsesMetadata: ResponseMetadataMap;
	};

	private loadingResponses: boolean = false;
	private responses: { responseId: number; responseTitle: string; metadata: Metadata[] }[] = [];
	private modalVisible: boolean = false;
	private deleteTaskModalState = {
		visible: false,
		responseId: 0,
		responseTitle: "",
		metadata: null,
	};

	/**
	 * Gets the displayTitle for the event this modal has been opened with.
	 */
	private get eventTitle() {
		return this.state.event?.displayTitle ?? "";
	}

	/**
	 * Called when the modal is first opened. Loads the responses from the
	 * data service so we can display their titles in the UI.
	 *
	 * Displays a loading icon whilst we're waiting for the response.
	 */
	@Watch("state.responsesMetadata")
	private async onResponsesChanged(newResponseMetadata: ResponseMetadataMap) {
		this.loadingResponses = true;

		let responseIds = Object.keys(newResponseMetadata);
		let responses = await Promise.all(
			responseIds.map(async (id) => {
				let responseId = +id;
				let response: Response = await api.responseGet(responseId);
				return {
					responseId: responseId,
					responseTitle: response.title,
					metadata: newResponseMetadata[responseId],
				};
			})
		);

		this.responses = responses;
		this.loadingResponses = false;
	}

	/**
	 * Handler for when the user wants to close the dialog. Calls setLinkedTaskModalShown
	 * and sets visible to false.
	 */
	private close() {
		this.setLinkedTaskModalShown({
			visible: false,
			event: null,
			responsesMetadata: null,
		});
	}

	/**
	 * Handler for when the users wants to delete a linked task. Updates deleteTaskModalState
	 * to display the confirmation modal, and sets the response/metadata to be deleted.
	 */
	private showDeleteTaskLinkModal(responseId: number, responseTitle: string, metadata: Metadata) {
		this.deleteTaskModalState = {
			visible: true,
			responseId,
			responseTitle,
			metadata,
		};
	}

	/**
	 * Handler for when the user confirms they want to delete a linked task. Performs the following
	 * steps:
	 * 	1. Deletes the metadata item from the store and sends a delete request to the data service.
	 * 	2. Deletes the response and metadata item from the linked task modal state (so it's removed from the UI).
	 * 	3. Creates an audit record and adds it to the event.
	 * 	4. Closes the modal.
	 */
	private async deleteTaskLink() {
		const { metadata, responseId, responseTitle } = this.deleteTaskModalState;

		// Delete the metadata from the store/DB
		await this.deleteMetadata(metadata);

		// Delete the linked task from our response metadata array
		let newResponsesMetadata = { ...this.state.responsesMetadata };
		let metadataForResponse = [...this.state.responsesMetadata[responseId]];
		metadataForResponse = metadataForResponse.filter((m) => m.metadataId != metadata.metadataId);

		if (metadataForResponse.length > 0) {
			// If there are tasks left linked, then populate the new object with them
			newResponsesMetadata[responseId] = metadataForResponse;
		} else {
			// If there aren't any tasks left linked to the response, delete it from the object
			delete newResponsesMetadata[responseId];
		}

		this.setLinkedTaskModalShown({
			visible: true,
			event: this.state.event,
			responsesMetadata: newResponsesMetadata,
		});

		// Create audit record
		const taskId = metadata.data.taskId;
		await this.createAuditRecord({
			eventId: this.state.event.eventID,
			eventRecordTypeId: 137,
			objectId: responseId,
			details: `Removed linked task ${taskId} from response: ${responseTitle}`,
		});

		// Close the confirmation modal
		this.deleteTaskModalState.visible = false;
	}
}
